import type { WidgetConfig, WidgetTheme } from '@lifi/widget'
import type { StoreApi, UseBoundStore } from 'zustand'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { addValueFromPathString } from '../../utils/addValue.js'
import { cloneStructuredConfig } from '../../utils/cloneStructuredConfig.js'
import type { ThemeItem } from '../editTools/types.js'
import type { WidgetConfigState } from './types.js'
import { getLocalStorageOutput } from './utils/getLocalStorageOutput.js'
import { getRehydratedConfigWithDefaultValues } from './utils/getRehydratedConfigWithDefaultValues.js'
import { setThemeAppearanceWithFallback } from './utils/setThemeWithFallback.js'

export const createWidgetConfigStore = (
  initialConfig: Partial<WidgetConfig>,
  themeItems: ThemeItem[],
  prefersDarkMode: boolean
): UseBoundStore<StoreApi<WidgetConfigState>> =>
  create<WidgetConfigState>()(
    persist(
      (set, get) => ({
        defaultConfig: initialConfig,
        config: {
          ...cloneStructuredConfig<Partial<WidgetConfig>>(initialConfig),
          providers: initialConfig.providers,
        },
        themeId: 'default',
        widgetThemeItems: themeItems,
        setConfig: (config) => {
          set({
            config: {
              ...get().config,
              ...config,
              // Preserve providers from current config if not provided in new config
              providers: config.providers ?? get().config?.providers,
            },
          })
        },
        setDefaultConfig: (defaultConfig) => {
          set({
            defaultConfig,
          })
        },
        resetConfig: () => {
          set({
            themeId: 'default',
            config: cloneStructuredConfig<Partial<WidgetConfig>>(
              get().defaultConfig!
            ),
          })
        },
        setAppearance: (appearance) => {
          set({
            config: {
              ...get().config,
              appearance,
            },
          })
        },
        setVariant: (variant) => {
          set({
            config: {
              ...get().config,
              variant,
            },
          })
        },
        setMode: (mode) => {
          set({
            config: {
              ...get().config,
              mode,
            },
          })
        },
        setChainSidebarDisabled: (disabled) => {
          set({
            config: {
              ...get().config,
              hiddenUI: {
                ...get().config?.hiddenUI,
                chainSidebar: disabled,
              },
            },
          })
        },
        setSplitOption: (option) => {
          set({
            config: {
              ...get().config,
              modeOptions: {
                ...get().config?.modeOptions,
                split: option,
              },
            },
          })
        },
        setBorderRadius: (radius) => {
          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                shape: {
                  ...get().config?.theme?.shape,
                  borderRadius: radius,
                },
              },
            } as WidgetConfig,
          })
        },
        resetBorderRadius: () => {
          const shape = get().config?.theme?.shape

          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                shape: {
                  ...(shape?.borderRadiusSecondary
                    ? {
                        borderRadiusSecondary: shape?.borderRadiusSecondary,
                      }
                    : {}),
                },
              },
            } as WidgetConfig,
          })
        },
        setBorderRadiusSecondary: (radius) => {
          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                shape: {
                  ...get().config?.theme?.shape,
                  borderRadiusSecondary: radius,
                },
              },
            } as WidgetConfig,
          })
        },
        resetBorderRadiusSecondary: () => {
          const shape = get().config?.theme?.shape

          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                shape: {
                  ...(shape?.borderRadius
                    ? {
                        borderRadius: shape?.borderRadius,
                      }
                    : {}),
                },
              },
            } as WidgetConfig,
          })
        },
        setColor: (path, color) => {
          set({
            config: addValueFromPathString<Partial<WidgetConfig>>(
              get().config,
              path,
              color
            ),
          })
        },
        setFontFamily: (fontFamily) => {
          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                typography: {
                  ...get().config?.theme?.typography,
                  fontFamily,
                },
              },
            } as WidgetConfig,
          })
        },
        setConfigTheme: (theme, themeId) => {
          // Preserve user's layout settings across theme switches
          const { height, maxHeight, display } =
            get().config?.theme?.container ?? {}
          set({
            themeId,
            config: {
              ...get().config,
              theme: {
                ...cloneStructuredConfig<Partial<WidgetTheme>>(theme),
                container: {
                  ...theme.container,
                  height,
                  maxHeight,
                  display,
                },
              },
            },
          })
        },
        setWalletConfig: (walletConfig?) => {
          set({
            config: {
              ...get().config,
              walletConfig,
            } as WidgetConfig,
          })
        },
        setAvailableThemes: (themeItems) => {
          set({
            widgetThemeItems: themeItems,
          })
        },
        getCurrentThemePreset: () => {
          const selectedThemeItem = get().widgetThemeItems.find(
            (themeItem) => themeItem.id === get().themeId
          )

          if (!selectedThemeItem) {
            return
          }

          return selectedThemeItem.theme
        },
        getCurrentConfigTheme: () => {
          return get().config?.theme
        },
        setHeader: (header) => {
          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                header,
              },
            },
          })
        },
        setContainer: (container) => {
          set({
            config: {
              ...get().config,
              theme: {
                ...get().config?.theme,
                container,
              },
            },
          })
        },
        setFormValues: (formValues) => {
          const config = get().config ?? {}

          // we remove the updatable form values as we only want pass properties with
          // updated values. Only updated values should be specified in the config (even if that a value of undefined)
          ;[
            'fromAmount',
            'fromChain',
            'fromToken',
            'toAddress',
            'toChain',
            'toToken',
          ].forEach((key) => {
            delete config[key as keyof WidgetConfig]
          })

          set({
            config: {
              ...get().config,
              ...(formValues as Partial<WidgetConfig>),
            },
          })
        },
      }),
      {
        name: 'li.fi-playground-config',
        version: 3,
        migrate: () => ({}),
        partialize: (state) => ({
          config: state?.config
            ? getLocalStorageOutput(state.config)
            : undefined,
          themeId: state.themeId,
        }),
        onRehydrateStorage: () => {
          return (state) => {
            if (state) {
              // state.config = the values taken from local storage by the partialize function
              // state.defaultConfig = the values from the default config file
              // The Partialize function only deals with values that the design interface can set
              //   so we need to restore values from the default config.
              if (state.config && state.defaultConfig) {
                const rehydratedConfigWithDefaultValues =
                  getRehydratedConfigWithDefaultValues(
                    state.config,
                    state.defaultConfig
                  )
                // Restore providers from defaultConfig since they're not persisted
                const configWithProviders = {
                  ...rehydratedConfigWithDefaultValues,
                  providers:
                    state.defaultConfig.providers ??
                    rehydratedConfigWithDefaultValues.providers,
                }
                state.setConfig(configWithProviders)
              }

              if (state.config?.walletConfig) {
                const walletConfig = state.defaultConfig?.walletConfig
                  ? state.defaultConfig?.walletConfig
                  : { ...state.config?.walletConfig, onConnect: () => {} }
                state.setWalletConfig(walletConfig)
              }

              setThemeAppearanceWithFallback(state, prefersDarkMode)
            }
          }
        },
      }
    )
  )
